/**
 * AchievementsPageHeader Component
 * Reusable header section for achievements page with icon, title, and description
 */

import React from 'react'
import { Box, Typography } from '@mui/material'
import { TrophyIcon } from '@heroicons/react/24/outline'
import { useTranslation } from '@/shared/hooks/useIntractionTranslate'
import { INTERAX_COLORS } from '../../../../constants/colors'

export interface AchievementsPageHeaderProps {
  /**
   * Optional custom title (defaults to translation key)
   */
  title?: string
  /**
   * Optional custom description (defaults to translation key)
   */
  description?: string
}

/**
 * AchievementsPageHeader - Header section with icon, title, and description
 * Used consistently across loading, error, and main states
 */
export const AchievementsPageHeader: React.FC<AchievementsPageHeaderProps> = ({ title, description }) => {
  const { t } = useTranslation()

  const headerTitle = title ?? t('gamer.achievements.title', 'Achievements')
  const headerDescription =
    description ?? t('gamer.achievements.section.description', 'Here you can see your achievements and progress')

  return (
    <Box sx={{ order: 0, width: '100%', flex: 'none', alignSelf: 'stretch' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 2
        }}
      >
        <Box
          sx={{
            width: '48px',
            height: '48px',
            borderRadius: '10px',
            backgroundColor: INTERAX_COLORS.sun20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          <TrophyIcon style={{ width: '24px', height: '24px', color: INTERAX_COLORS.sun }} />
        </Box>
        <Box>
          {(() => {
            const words = headerTitle.trim().split(/\s+/)
            const firstWord = words.length > 1 ? words[0] : ''
            const rest = words.length > 1 ? words.slice(1).join(' ') : words[0] || ''

            return (
              <Box
                sx={{
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  margin: 0,
                  marginBottom: '4px'
                }}
              >
                {/* First word in white (if multiple words) */}
                {firstWord && (
                  <Typography
                    sx={{
                      fontFamily: 'Lexend Deca',
                      fontStyle: 'normal',
                      fontWeight: 600,
                      fontSize: '26px',
                      lineHeight: '34px',
                      color: INTERAX_COLORS.white,
                      margin: 0
                    }}
                  >
                    {firstWord}
                  </Typography>
                )}
                {/* Rest of name in gradient */}
                {rest && (
                  <Typography
                    sx={{
                      fontFamily: 'Lexend Deca',
                      fontStyle: 'normal',
                      fontWeight: 600,
                      fontSize: '26px',
                      lineHeight: '34px',
                      background: 'linear-gradient(360deg, #FFD196 -15.91%, #FFAB40 1.14%, #FFFFFF 56.35%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      textFillColor: 'transparent',
                      textShadow: '0px 0px 10px rgba(0, 0, 0, 0.5), 0px 0px 20px rgba(255, 171, 64, 0.4)',
                      margin: 0
                    }}
                  >
                    {rest}
                  </Typography>
                )}
              </Box>
            )
          })()}
          <Typography
            sx={{
              fontFamily: 'Lexend Deca',
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '20px',
              color: INTERAX_COLORS.thunderGrey,
              margin: 0
            }}
          >
            {headerDescription}
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

export default AchievementsPageHeader
